import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/common/components/ui/button";

import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { notify } from "@/common/components/common/Notification";
import { IPOGetByIdResponse, IPOCreatePayload } from "@/utils/types/IPO/ipo";
import { parse, format } from "date-fns";
import IPODetailsTab from "./tabs/IPODetailsTab";
import { getFieldType } from "./utils/validation";
import Loader from "@/common/components/ui/loader";
import CommonModal from "@/common/components/ui/modal";
// import CommonModal from "@/common/components/common/CommonModal";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import UploadService from "@/services/APIs/UploadService";
import FileUploadField from "./components/FileUploadField";
import IPOService from "@/services/IPO/APIs/IPOService";

const AdminIPOForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getListingStatusColor = (status: string) => {
    switch (status) {
      case "open":
        return "border-emerald-100 bg-emerald-50 text-emerald-600";
      case "listed":
        return "border-blue-100 bg-blue-50 text-blue-600";
      default:
        return "border-amber-100 bg-amber-50 text-amber-600";
    }
  };
  const { id } = useParams();
  const isEdit = !!id;
  const isViewMode = location.pathname.includes("/view/");

  const [loading, setLoading] = useState(isEdit || isViewMode);
  const [ipoData, setIpoData] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // RHP Document Upload Modal State
  const [isRHPModalOpen, setIsRHPModalOpen] = useState(false);
  const [rhpUploadData, setRHPUploadData] = useState({
    iseNumber: "",
    file: null as File | null,
  });
  const [rhpUploading, setRHPUploading] = useState(false);
  const [fetchingAI, setFetchingAI] = useState(false);

  // Form state to store all input values
  const [formData, setFormData] = useState<any>({
    company_name: "",
    company_short_name: "",
    nse_symbol: "",
    bse_symbol: "",
    ipo_name: "",
    isin: "",
    bse_group: "",
    industry: "",
    managing_director: "",
    parent_organization: "",
    about_company: "",
    website: "",
    headquarters: "",
    company_phone: "",
    company_email: "",
    company_address: "",
    logo_url: "",
    rhp_url: "",
    drhp_url: "",
    ipo_type: "Main Board",
    status: "Upcoming",
    founded_year: "",
    employees: "",
    price_min: "",
    price_max: "",
    lot_size: "",
    face_value: "10",
    issue_size: "",
    fresh_issue: "",
    ofs: "",
    issue_type: "Book Built",
    pre_issue_shares: "",
    post_issue_shares: "",
    pre_issue_holding: "",
    post_issue_holding: "",
    qib_shares_offered: "",
    nii_shares_offered: "",
    retail_shares_offered: "",
    anchor_shares_offered: "",
    employee_shares_offered: "",
    market_maker_shares_offered: "",
    market_maker_portion: "",
    employee_discount: "",
    cutoff_time_upi: "",
    pe_ratio: "",
    pbv_ratio: "",
    roe: "",
    roce: "",
    pat_margin: "",
    debt_equity: "",
    eps_pre: "",
    gmp: "",
    expected_listing: "",
    listing_gain: "",
    subscription_total: "",
    subscription_retail: "",
    reservations_qib: "",
    reservations_nii: "",
    reservations_retail: "",
    reservations_employee: "",
    registrar_name: "",
    registrar_phone: "",
    registrar_email: "",
    registrar_website: "",
    lead_managers: "",
    market_maker: "",
    retail_min_lot: "",
    retail_min_shares: "",
    retail_min_amount: "",
    snii_lots: "",
    snii_min_investment: "",
    snii_min_shares: "",
    bnii_lots: "",
    bnii_min_investment: "",
    bnii_min_shares: "",
  });

  // Dynamic Lists State
  const [financials, setFinancials] = useState<any[]>([]);
  /** companyFinancials.amountUnit from API */
  const [financialSummaryUnit, setFinancialSummaryUnit] = useState("");
  /** ipoObjectsOfIssue.amountUnit from API */
  const [objectsOfIssueAmountUnit, setObjectsOfIssueAmountUnit] = useState("");
  const [peers, setPeers] = useState<any[]>([]);
  const [objectives, setObjectives] = useState<any[]>([]);
  const [promoters, setPromoters] = useState<any[]>([]);

  // Date fields state (stored in dd/MM/yyyy format for DatePickerInput)
  const [bidStartDate, setBidStartDate] = useState<string>("");
  const [bidEndDate, setBidEndDate] = useState<string>("");
  const [allotmentDate, setAllotmentDate] = useState<string>("");
  const [refundDate, setRefundDate] = useState<string>("");
  const [creditToDematDate, setCreditToDematDate] = useState<string>("");
  const [listingDate, setListingDate] = useState<string>("");

  // Handler to update form data with type restrictions and validation
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const fieldType = getFieldType(name);

    // Restrict input based on field type
    let processedValue: string | number = value;

    if (fieldType === "number") {
      // For number fields, only allow digits, decimal point, and minus sign
      if (value === "" || value === "-") {
        processedValue = value;
      } else if (/^-?\d*\.?\d*$/.test(value)) {
        processedValue = value;
      } else {
        // Invalid input for number field, don't update
        return;
      }
    } else {
      // For string fields, allow all characters
      processedValue = value;
    }

    setFormData((prev: any) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handler for Select components
  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Helper function to convert yyyy-MM-dd to dd/MM/yyyy
  const convertToDisplayFormat = (
    dateStr: string | undefined | null
  ): string => {
    if (!dateStr) {
      return "";
    }
    try {
      // If already in dd/MM/yyyy format, return as is
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
        return dateStr;
      }

      // Handle ISO strings (remove time part)
      const cleanDateStr = dateStr.includes("T")
        ? dateStr.split("T")[0]
        : dateStr;

      // If in yyyy-MM-dd format, convert to dd/MM/yyyy
      if (/^\d{4}-\d{2}-\d{2}$/.test(cleanDateStr)) {
        const parsed = parse(cleanDateStr, "yyyy-MM-dd", new Date());
        return format(parsed, "dd/MM/yyyy");
      }
      return "";
    } catch {
      return "";
    }
  };

  // Helper function to convert dd/MM/yyyy to yyyy-MM-dd for form submission
  const convertToFormFormat = (dateStr: string): string => {
    if (!dateStr) {
      return "";
    }
    try {
      const parsed = parse(dateStr, "dd/MM/yyyy", new Date());
      return format(parsed, "yyyy-MM-dd");
    } catch {
      return "";
    }
  };

  useEffect(() => {
    if ((isEdit || isViewMode) && id) {
      fetchIPO();
    }
  }, [id, isViewMode]);

  // Auto-calculate allocation fields when price_max or lot_size changes
  useEffect(() => {
    const priceMaxNum = Number(formData.price_max) || 0;
    const lotSizeNum = Number(formData.lot_size) || 0;

    if (priceMaxNum > 0 && lotSizeNum > 0) {
      const updates: any = {};

      // Only update if field is empty or needs recalculation
      if (!formData.retail_min_lot || formData.retail_min_lot === "") {
        updates.retail_min_lot = "1";
      }
      if (!formData.retail_min_shares || formData.retail_min_shares === "") {
        updates.retail_min_shares = String(
          lotSizeNum * (Number(formData.retail_min_lot) || 1)
        );
      }
      if (!formData.retail_min_amount || formData.retail_min_amount === "") {
        updates.retail_min_amount = String(
          priceMaxNum * lotSizeNum * (Number(formData.retail_min_lot) || 1)
        );
      }
      if (!formData.snii_lots || formData.snii_lots === "") {
        updates.snii_lots = "14";
      }
      if (
        !formData.snii_min_investment ||
        formData.snii_min_investment === ""
      ) {
        updates.snii_min_investment = String(priceMaxNum * lotSizeNum * 14);
      }
      if (!formData.snii_min_shares || formData.snii_min_shares === "") {
        updates.snii_min_shares = String(lotSizeNum * 14);
      }
      if (!formData.bnii_lots || formData.bnii_lots === "") {
        updates.bnii_lots = "67";
      }
      if (
        !formData.bnii_min_investment ||
        formData.bnii_min_investment === ""
      ) {
        updates.bnii_min_investment = String(priceMaxNum * lotSizeNum * 67);
      }
      if (!formData.bnii_min_shares || formData.bnii_min_shares === "") {
        updates.bnii_min_shares = String(lotSizeNum * 67);
      }

      if (Object.keys(updates).length > 0) {
        setFormData((prev: any) => ({
          ...prev,
          ...updates,
        }));
      }
    }
  }, [formData.price_max, formData.lot_size]);

  const fetchIPO = async () => {
    try {
      const response = await IPOService.getIPOById(id!);
      const data: IPOGetByIdResponse = response;
      const freshIssue =
        (Number(data.issueSize) || 0) - (Number(data.offerForSale) || 0);
      setIpoData(data);

      // Set nested data to state - currently API response structure for nested data is not fully clear in provided type,
      // assuming they might come as separate calls or are not yet in the main response based on the new type definition.
      // If the backend sends them in the same object but they are not in the interface, we might need to adjust.
      // For now, retaining existing logic if properties exist on 'data' (casting to any if needed) or initializing empty.

      // Financial Performance = companyFinancials.financialByPeriod (all rows, full columns)
      let mappedFinancials: any[] = [];
      if (data.companyFinancials?.financialByPeriod?.length) {
        setFinancialSummaryUnit(data.companyFinancials.amountUnit ?? "");
        mappedFinancials = data.companyFinancials.financialByPeriod.map(
          period => ({
            period_ended: period.periodEnded ?? "",
            total_income: period.totalIncome ?? "",
            assets: period.assets ?? "",
            ebitda: period.ebitda ?? "",
            profit_after_tax: period.profitAfterTax ?? "",
            net_worth: period.netWorth ?? "",
            reserves_and_surplus: period.reservesAndSurplus ?? "",
            total_borrowing: period.totalBorrowing ?? "",
          })
        );
      } else {
        setFinancialSummaryUnit("");
        const legacy = [
          {
            period_ended: "Year 1",
            total_income: data.revenue1 ?? "",
            assets: data.totalAssets1 ?? "",
            ebitda: "",
            profit_after_tax: data.totalProfit1 ?? "",
            net_worth: "",
            reserves_and_surplus: "",
            total_borrowing: "",
          },
          {
            period_ended: "Year 2",
            total_income: data.revenue2 ?? "",
            assets: data.totalAssets2 ?? "",
            ebitda: "",
            profit_after_tax: data.totalProfit2 ?? "",
            net_worth: "",
            reserves_and_surplus: "",
            total_borrowing: "",
          },
          {
            period_ended: "Year 3",
            total_income: data.revenue3 ?? "",
            assets: data.totalAssets3 ?? "",
            ebitda: "",
            profit_after_tax: data.totalProfit3 ?? "",
            net_worth: "",
            reserves_and_surplus: "",
            total_borrowing: "",
          },
        ].filter(
          f =>
            f.total_income ||
            f.assets ||
            f.profit_after_tax ||
            f.ebitda ||
            f.net_worth
        );
        mappedFinancials = legacy;
      }

      const anyData = data as any;
      setFinancials(
        mappedFinancials.length > 0
          ? mappedFinancials
          : (anyData.financial_data ?? [])
      );
      setPeers(anyData.peer_data ?? []);

      // Objects of the Issue = ipoObjectsOfIssue.items
      if (data.ipoObjectsOfIssue?.items?.length) {
        setObjectsOfIssueAmountUnit(data.ipoObjectsOfIssue.amountUnit ?? "");
        setObjectives(
          data.ipoObjectsOfIssue.items.map(
            (item: {
              serialNo: number;
              issueObject: string;
              estimatedAmount: number | null;
            }) => ({
              serialNo: item.serialNo,
              issueObject: item.issueObject ?? "",
              estimatedAmount: item.estimatedAmount ?? "",
            })
          )
        );
      } else {
        setObjectsOfIssueAmountUnit("");
        setObjectives(anyData.objective_data ?? []);
      }
      // API returns companyPromoters as string[], DynamicList expects { name }[]
      const rawPromoters: unknown =
        data.companyPromoters ?? anyData.promoter_data;
      if (Array.isArray(rawPromoters) && rawPromoters.length > 0) {
        setPromoters(
          rawPromoters.map((p: unknown) =>
            typeof p === "string" ? { name: p } : p
          )
        );
      } else {
        setPromoters([]);
      }

      // Set date fields in display format (dd/MM/yyyy)
      // The API returns dates in presumably mostly ready format, but we ensure display format.
      // Mapping:
      // anchor_bidding_date (not creating in new response? Using biddingStartDate - 1 day or custom?) -> new response doesn't hav anchor date explicit?
      // Use existing logic if field matches or default.

      // Mapping based on new Interface:
      // biddingStartDate -> opening_date
      // biddingEndDate -> closing_date
      // allotmentDate -> basis_of_allotment_date
      // refund_Initiation -> refund_initiation_date
      // creditToDAccount -> credit_to_demat_date
      // ipoListingDate -> listing_date

      setBidStartDate(convertToDisplayFormat(data.biddingStartDate));
      setBidEndDate(convertToDisplayFormat(data.biddingEndDate));
      setAllotmentDate(convertToDisplayFormat(data.allotmentDate));
      setRefundDate(convertToDisplayFormat(data.refund_Initiation));
      setCreditToDematDate(convertToDisplayFormat(data.creditToDAccount));
      setListingDate(convertToDisplayFormat(data.ipoListingDate));

      // Populate form data state (use nested objects when present)
      const registrarName = data.ipoRegistrar?.name ?? data.nsE_Registrar ?? "";
      const registrarEmail =
        data.ipoRegistrar?.email ?? data.companyEmail ?? "";
      const registrarPhone =
        data.ipoRegistrar?.phoneNumbers?.[0] ?? data.registrarNumber ?? "";
      const registrarWebsite =
        data.ipoRegistrar?.website ?? data.registrarWebsite ?? "";
      const companyPhone =
        data.companyContactDetails?.phoneNumbers?.[0] ??
        data.registrarNumber ??
        "";
      const companyEmail =
        data.companyContactDetails?.email ?? data.companyEmail ?? "";
      const companyAddress =
        data.companyContactDetails?.addressLines?.join(", ") ?? "";
      const leadManagers = data.ipoLeadManagers?.managers?.join(", ") ?? "";

      // ── Extract from ipoReservation.categories (preferred) ──────────────────
      // Helper: find a category by searching through keywords.
      const findCat = (keywords: string[]) => {
        const categories = data.ipoReservation?.categories ?? [];
        // Priority 1: Exact match for any keyword (ignoring case)
        const exactMatch = categories.find(c =>
          keywords.some(
            kw => c.categoryName.trim().toLowerCase() === kw.toLowerCase()
          )
        );
        if (exactMatch) {
          return exactMatch;
        }

        // Priority 2: Keyword as a part of the name
        return categories.find(c => {
          const lowerName = c.categoryName.toLowerCase();
          return keywords.some(kw => lowerName.includes(kw.toLowerCase()));
        });
      };

      const qibCat = findCat(["qib"]);
      const niiCat = findCat(["nii", "hni"]);
      const retailCat = findCat(["retail", "rii"]);
      const anchorCat = findCat(["anchor"]);
      const mmCat = findCat(["market maker", "mm"]);
      const employeeCat = findCat(["employee"]);

      setFormData({
        company_name: data.name ?? "",
        company_short_name: data.name ?? "",
        nse_symbol: data.symbol ?? "",
        bse_symbol: "",
        ipo_name: data.name ?? "",
        isin: data.isin ?? "",
        bse_group: "",
        industry: data.industrySector ?? "",
        managing_director: data.managingDirector ?? "",
        parent_organization: data.parentOrganisation ?? "",
        about_company: data.aboutCompany ?? "",
        website: data.companyWebsite ?? "",
        headquarters: data.companyHeadquarters ?? "",
        company_phone: companyPhone,
        company_email: companyEmail,
        company_address: companyAddress,
        logo_url: data.ipoLogo ?? "",
        rhp_url: data.ipoDoc ?? "",
        drhp_url: "",
        ipo_type: data.nsE_SubType === "Main board" ? "Main Board" : "SME",
        status: data.isPublish ? "Open" : "Upcoming",
        founded_year: data.foundedIN ?? "",
        employees: data.totalEmployees ?? "",
        price_min: data.minPrice ?? "",
        price_max: data.maxPrice ?? "",
        lot_size: String(data.lotSize ?? ""),
        face_value: data.faceValue ?? "10",
        issue_size: String(data.issueSize ?? ""),
        fresh_issue: String(freshIssue),
        ofs: data.offerForSale ?? "",
        issue_type: data.issueType ?? "",
        pre_issue_shares: String(data.preIssueShares ?? ""),
        post_issue_shares: String(data.postIssueShare ?? ""),
        pre_issue_holding: data.preIssueHolding ?? "",
        post_issue_holding: data.postIssueHolding ?? "",
        // Shares Offered — prefer ipoReservation.categories.sharesOffered
        qib_shares_offered: qibCat
          ? String(qibCat.sharesOffered)
          : (data.qib ?? ""),
        nii_shares_offered: niiCat
          ? String(niiCat.sharesOffered)
          : (data.nii ?? ""),
        retail_shares_offered: retailCat
          ? String(retailCat.sharesOffered)
          : (data.rii ?? ""),
        anchor_shares_offered: anchorCat
          ? String(anchorCat.sharesOffered)
          : (data.anchorShare ?? ""),
        employee_shares_offered: employeeCat
          ? String(employeeCat.sharesOffered)
          : (data.employee ?? ""),
        market_maker_shares_offered: mmCat
          ? String(mmCat.sharesOffered)
          : data.marketMakerShares !== null
            ? String(data.marketMakerShares)
            : "",
        // Reservation % — prefer ipoReservation.categories.percentage
        market_maker_portion: mmCat
          ? String(mmCat.percentage)
          : (data.marketMakerPortion ?? ""),
        employee_discount: "",
        cutoff_time_upi: "",
        pe_ratio: data.peRatio ?? "",
        pbv_ratio: data.pbvRatio ?? "",
        roe: data.roe ?? "",
        roce: data.roce ?? "",
        pat_margin: data.patMargin ?? "",
        debt_equity: data.debtEquityRatio ?? "",
        eps_pre: data.eps ?? "",
        gmp: "",
        expected_listing: "",
        listing_gain: "",
        subscription_total: "",
        subscription_retail: "",
        // Reservation % — prefer ipoReservation.categories.percentage
        reservations_qib: qibCat
          ? String(qibCat.percentage)
          : (data.qibPortion ?? ""),
        reservations_nii: niiCat
          ? String(niiCat.percentage)
          : (data.niiPortion ?? ""),
        reservations_retail: retailCat
          ? String(retailCat.percentage)
          : (data.retailPortion ?? ""),
        reservations_employee: employeeCat
          ? String(employeeCat.percentage)
          : (data.employeePortion ?? ""),
        registrar_name: registrarName,
        registrar_phone: registrarPhone,
        registrar_email: registrarEmail,
        registrar_website: registrarWebsite,
        lead_managers: leadManagers,
        market_maker:
          data.marketMakerShares !== null ? String(data.marketMakerShares) : "",
        retail_min_lot: String(data.retailMinLot ?? ""),
        retail_min_shares: String(data.retailMinShares ?? ""),
        retail_min_amount: String(data.retailMinAmount ?? ""),
        snii_lots: String(data.shniMinLot ?? ""),
        snii_min_investment: String(data.shniMinAmount ?? ""),
        snii_min_shares: String(data.shniMinShares ?? ""),
        bnii_lots: String(data.bhniMinLot ?? ""),
        bnii_min_investment: String(data.bhniMinAmount ?? ""),
        bnii_min_shares: String(data.bhniMinShares ?? ""),
      });
    } catch (error: any) {
      console.error("Error fetching IPO:", error);
      notify.error(error?.message ?? "Failed to load IPO data");
      navigate("/admin/ipo-management");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Temporarily bypass client-side validation and always attempt submit.
    setValidationErrors({});

    const data = formData;

    const toNum = (val: any) =>
      val === "" || val === undefined || val === null ? null : Number(val);
    const toString = (val: any) =>
      val === "" || val === undefined || val === null ? null : String(val);

    const formatDateForPayload = (displayDate: string) => {
      const isoDate = convertToFormFormat(displayDate);
      return isoDate ? `${isoDate}T00:00:00` : null;
    };

    // New payload structure matching the user's request
    const payload: IPOCreatePayload = {
      ipoMasterID: isEdit && ipoData?.ipoMasterID ? ipoData.ipoMasterID : 1000,
      symbol: toString(data.nse_symbol) ?? "",
      ipoLogo: toString(data.logo_url) ?? "",
      name: toString(data.company_name) ?? "",
      lotSize: toNum(data.lot_size) ?? 0,
      minBidQuantity: toNum(data.retail_min_shares),
      faceValue: toNum(data.face_value),
      minPrice: toNum(data.price_min) ?? 0,
      maxPrice: toNum(data.price_max) ?? 0,
      tickSize: null,
      cutoffPrice: null,
      biddingStartDate: formatDateForPayload(bidStartDate) ?? "",
      biddingEndDate: formatDateForPayload(bidEndDate) ?? "",
      nsE_DailyStartTime: null,
      industrySector: toString(data.industry) ?? "",
      nsE_DailyEndTime: null,
      isin: toString(data.isin) ?? "",
      issueSize: toNum(data.issue_size) ?? 0,
      nsE_SubType:
        (data.ipo_type as string) === "Main Board" ? "Main board" : "SME",
      nsE_Registrar: toString(data.registrar_name),
      t1ModStartDate: null,
      t1ModEndDate: null,
      t1ModStartTime: null,
      t1ModEndTime: null,
      issueType: "EQUITY",
      caCode: null,
      nsE_SubCatCode: null,
      minValue: null,
      maxValue: null,
      allowCutOff: false,
      discountType: null,
      discountPrice: null,
      originalPrice: toNum(data.price_max),
      maxQuantity: null,
      nsE_AllowUpi: false,
      nsE_MaxUpiLimit: null,
      asbanonasba: null,
      nsE_ERRORCODE: null,
      nsE_MESSAGE: null,
      isPublish: true,
      applicationStartNo: 1,
      applicationEndNo: 1000,
      lastApplicationNo: 110,
      ipoDoc: toString(data.rhp_url) ?? "",
      allotmentDate: formatDateForPayload(allotmentDate),
      refund_Initiation: formatDateForPayload(refundDate),
      creditToDAccount: formatDateForPayload(creditToDematDate),
      ipoListingDate: formatDateForPayload(listingDate),
      qib: toNum(data.qib_shares_offered),
      rii: toNum(data.retail_shares_offered),
      nii: toNum(data.nii_shares_offered),
      employee: toNum(data.employee_shares_offered),
      shareHolders: null,
      total: null,
      retailMinLot: toNum(data.retail_min_lot),
      retailMinShares: toNum(data.retail_min_shares),
      retailMinAmount: toNum(data.retail_min_amount),
      retailMaxLot: null,
      retailMaxShares: null,
      retailMaxAmount: null,
      shniMinLot: toNum(data.snii_lots),
      shniMinShares: toNum(data.snii_min_shares),
      shniMinAmount: toNum(data.snii_min_investment),
      bhniMinLot: toNum(data.bnii_lots),
      bhniMinShares: toNum(data.bnii_min_shares),
      bhniMinAmount: toNum(data.bnii_min_investment),
      aboutCompany: toString(data.about_company),
      foundedIN: toString(data.founded_year),
      managingDirector: toString(data.managing_director),
      parentOrganisation: toString(data.parent_organization),
      revenue1:
        toString(financials[0]?.total_income ?? financials[0]?.total_revenue) ??
        null,
      revenue2:
        toString(financials[1]?.total_income ?? financials[1]?.total_revenue) ??
        null,
      revenue3:
        toString(financials[2]?.total_income ?? financials[2]?.total_revenue) ??
        null,
      totalAssets1:
        toString(financials[0]?.assets ?? financials[0]?.total_assets) ?? null,
      totalAssets2:
        toString(financials[1]?.assets ?? financials[1]?.total_assets) ?? null,
      totalAssets3:
        toString(financials[2]?.assets ?? financials[2]?.total_assets) ?? null,
      totalProfit1: toString(financials[0]?.profit_after_tax) ?? null,
      totalProfit2: toString(financials[1]?.profit_after_tax) ?? null,
      totalProfit3: toString(financials[2]?.profit_after_tax) ?? null,
      companyFinancials: {
        amountUnit: financialSummaryUnit || "₹ Crore",
        financialByPeriod: financials
          .map(row => ({
            periodEnded: String(row.period_ended ?? row.year ?? "").trim(),
            totalIncome: toNum(row.total_income ?? row.total_revenue),
            assets: toNum(row.assets ?? row.total_assets),
            ebitda: toNum(row.ebitda),
            profitAfterTax: toNum(row.profit_after_tax),
            netWorth: toNum(row.net_worth),
            totalBorrowing: toNum(row.total_borrowing),
            reservesAndSurplus: toNum(row.reserves_and_surplus),
          }))
          .filter(
            p =>
              p.periodEnded ||
              p.totalIncome !== null ||
              p.assets !== null ||
              p.ebitda !== null ||
              p.profitAfterTax !== null ||
              p.netWorth !== null ||
              p.totalBorrowing !== null ||
              p.reservesAndSurplus !== null
          ),
      },
      ipoObjectsOfIssue: {
        amountUnit: objectsOfIssueAmountUnit ?? "₹ Cr.",
        totalEstimatedAmount:
          objectives.reduce(
            (sum, row) =>
              sum +
              (Number.isFinite(Number(row.estimatedAmount))
                ? Number(row.estimatedAmount)
                : 0),
            0
          ) ?? null,
        items: objectives
          .filter(
            row =>
              (row.issueObject && String(row.issueObject).trim()) ??
              (row.estimatedAmount !== null || row.serialNo !== null)
          )
          .map((row, idx) => ({
            serialNo: Number.isFinite(Number(row.serialNo))
              ? Number(row.serialNo)
              : idx + 1,
            issueObject: String(row.issueObject ?? "").trim(),
            estimatedAmount: toNum(row.estimatedAmount),
          })),
      },
      disclaimer: null,
      exchange: "NSE",
      isDeleted: false,
      hniApplicationStartNo: 1500,
      hniApplicationEndNo: 28000,
      hniLastApplicationNo: 30012,
    };

    console.log(payload);

    try {
      setLoading(true);
      if (isEdit) {
        // Warning: The update method might expect the old payload structure.
        // If the backend has changed fully, we might need a new updateIPO method.
        // For now, using the same payload structure if possible, or we should use createIPO?
        // Assuming user wants to use createIPO for now as per instructions.
        await IPOService.update(id as string, payload as any);
        notify.success(
          "IPO Updated",
          "All fields and nested data saved successfully."
        );
        navigate("/admin/ipo-management");
      } else {
        await IPOService.createIPO(payload);
        notify.success("IPO Added", "Listing published with all details.");
        navigate("/admin/ipo-management");
      }
    } catch (error: any) {
      console.error("Error saving IPO:", error);
      const errorMessage =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to save IPO listing";

      // If error response has field-specific errors, set them
      if (error?.response?.data) {
        const apiErrors: Record<string, string> = {};
        Object.keys(error.response.data).forEach(key => {
          const fieldName = key;
          const errorValue = error.response.data[key];
          if (Array.isArray(errorValue)) {
            apiErrors[fieldName] = errorValue[0];
          } else if (typeof errorValue === "string") {
            apiErrors[fieldName] = errorValue;
          }
        });

        if (Object.keys(apiErrors).length > 0) {
          setValidationErrors(apiErrors);
          // Scroll to first error
          const firstErrorField = document.querySelector(
            `[name="${Object.keys(apiErrors)[0]}"]`
          );
          if (firstErrorField) {
            firstErrorField.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            (firstErrorField as HTMLElement).focus();
          }
        }
      }

      notify.error(errorMessage);
      // DO NOT navigate on error - stay on the form page
    } finally {
      setLoading(false);
    }
  };

  // RHP Document Upload Handlers
  const handleRHPApply = async () => {
    if (!rhpUploadData.file) {
      notify.error("Please select a file to upload");
      return;
    }

    setRHPUploading(true);
    try {
      const response = await UploadService.uploadFiles({
        folderName: "ipo-documents",
        files: [rhpUploadData.file],
      });

      const fileUrl =
        response?.data?.url ?? response?.url ?? response?.file_url;
      if (fileUrl) {
        // Update formData with the uploaded RHP URL
        setFormData((prev: any) => ({
          ...prev,
          rhp_url: fileUrl,
        }));
        notify.success("RHP Document uploaded successfully!");
        setIsRHPModalOpen(false);
        setRHPUploadData({ iseNumber: "", file: null });
      } else {
        notify.error("Upload failed: Invalid response");
      }
    } catch (error: any) {
      console.error(error);
      notify.error(error?.message ?? "Upload failed");
    } finally {
      setRHPUploading(false);
    }
  };

  const handleRHPModalClose = () => {
    setIsRHPModalOpen(false);
    setRHPUploadData({ iseNumber: "", file: null });
  };

  const handleFetchFromAI = async () => {
    if (!id) {
      notify.error("IPO ID is required to fetch details from AI.");
      return;
    }
    try {
      setFetchingAI(true);
      const response = await IPOService.IPODetilsFetchFromAI(id);

      if (response?.success) {
        setFormData((prev: any) => ({
          ...prev,
          ...response?.data,
        }));
        notify.success(
          "Success",
          response?.message ?? "Successfully fetched IPO details from AI."
        );
        fetchIPO();
      } else {
        notify.error(response?.message ?? "Failed to fetch details from AI");
      }
    } catch (error: any) {
      console.error("Error fetching from AI:", error);
      notify.error(error?.message ?? "Failed to fetch details from AI");
    } finally {
      setFetchingAI(false);
    }
  };

  if (loading && isEdit) {
    return <Loader message="Loading IPO data..." />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50">
      {loading && (
        <Loader message={isEdit ? "Updating IPO..." : "Creating IPO..."} />
      )}
      {fetchingAI && <Loader message="Fetching IPO details from AI..." />}
      <form
        id="ipo-form"
        onSubmit={handleSubmit}
        className="flex h-full flex-col"
      >
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="mx-auto w-full max-w-7xl">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-5">
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() => navigate("/admin/ipo-management")}
                  className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-5 w-5 text-slate-600" />
                </Button>
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-3">
                    <h1
                      className="min-w-0 flex-1 truncate text-xl font-bold tracking-tight text-slate-900"
                      title={
                        isViewMode
                          ? `View : ${ipoData?.name ?? "IPO"}`
                          : isEdit
                            ? `Edit : ${ipoData?.name ?? "IPO"}`
                            : "Create New IPO"
                      }
                    >
                      {isViewMode
                        ? `View : ${ipoData?.name ?? "IPO"}`
                        : isEdit
                          ? `Edit : ${ipoData?.name ?? "IPO"}`
                          : "Create New IPO"}
                    </h1>
                    {(ipoData?.ipoStatus ??
                      (ipoData as any)?.listing_status) && (
                      <span
                        className={`flex-shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${getListingStatusColor(
                          ipoData?.ipoStatus ??
                            (ipoData as any)?.listing_status ??
                            ""
                        )}`}
                      >
                        {(
                          ipoData?.ipoStatus ??
                          (ipoData as any)?.listing_status ??
                          ""
                        )
                          .charAt(0)
                          .toUpperCase() +
                          (
                            ipoData?.ipoStatus ??
                            (ipoData as any)?.listing_status ??
                            ""
                          ).slice(1)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-3">
                {isViewMode ? (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => navigate("/admin/ipo-management")}
                    className="h-10 bg-primary px-5 text-sm font-semibold text-white"
                  >
                    Back to List
                  </Button>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleFetchFromAI}
                      disabled={fetchingAI || !isEdit}
                      className="h-8 gap-1.5 px-3 text-xs font-medium text-slate-500 hover:bg-background"
                      title={
                        !isEdit
                          ? "Create IPO data first before fetching from AI"
                          : ""
                      }
                    >
                      {fetchingAI ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : null}
                      Fetch from AI
                    </Button>
                    {/* <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsRHPModalOpen(true)}
                      className="h-8 gap-1.5 px-3 text-xs font-medium text-slate-500 hover:bg-background"
                    >
                      <Upload className="h-3 w-3" />
                      Upload RHP Document
                    </Button> */}
                    <Button
                      type="submit"
                      disabled={loading}
                      size="sm"
                      className="h-8 gap-1.5 bg-primary px-3 text-xs font-medium text-white hover:bg-primary/95"
                    >
                      {loading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Save className="h-3 w-3" />
                      )}
                      {isEdit ? "Save" : "Publish IPO"}
                    </Button>
                  </>
                )}
              </div>
            </div>
            {/* Tab navigation moved into IPODetailsTab */}
          </div>
        </div>

        <div className="flex w-full flex-1 overflow-auto bg-slate-50/50">
          <div className="mx-auto w-full max-w-7xl flex-1 px-6 py-6">
            <div className="w-full space-y-6">
              <div className="m-0 space-y-6 duration-300 animate-in fade-in slide-in-from-bottom-2">
                <IPODetailsTab
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleSelectChange={handleSelectChange}
                  validationErrors={validationErrors}
                  readOnly={isViewMode}
                  bidStartDate={bidStartDate}
                  bidEndDate={bidEndDate}
                  allotmentDate={allotmentDate}
                  refundDate={refundDate}
                  listingDate={listingDate}
                  setBidStartDate={setBidStartDate}
                  setBidEndDate={setBidEndDate}
                  setAllotmentDate={setAllotmentDate}
                  setRefundDate={setRefundDate}
                  setListingDate={setListingDate}
                  convertToFormFormat={convertToFormFormat}
                  financials={financials}
                  setFinancials={setFinancials}
                  peers={peers}
                  setPeers={setPeers}
                  objectives={objectives}
                  setObjectives={setObjectives}
                  promoters={promoters}
                  setPromoters={setPromoters}
                  financialAmountUnit={financialSummaryUnit}
                  onFinancialAmountUnitChange={setFinancialSummaryUnit}
                  objectsOfIssueAmountUnit={objectsOfIssueAmountUnit}
                  onObjectsOfIssueAmountUnitChange={setObjectsOfIssueAmountUnit}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
      {/* RHP Document Upload Modal */}
      <CommonModal
        isOpen={isRHPModalOpen}
        onClose={handleRHPModalClose}
        width="w-[500px]"
      >
        <div className="mt-4 space-y-6 px-8 py-8">
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">
              ISE Number
            </Label>
            <Input
              value={rhpUploadData.iseNumber}
              onChange={e =>
                setRHPUploadData(prev => ({
                  ...prev,
                  iseNumber: e.target.value,
                }))
              }
              placeholder="Enter ISE number"
              className="h-10 border-slate-200 focus:border-primary/50 focus:ring-primary/10"
            />
          </div>

          <div className="space-y-2">
            <FileUploadField
              name="rhp_url"
              type="pdfs"
              label="RHP Document (PDF)"
              value={rhpUploadData.file?.name ?? "https://example.com/rhp.pdf"}
              onChange={(e: { target: { name: string; value: string } }) =>
                setRHPUploadData(prev => ({
                  ...prev,
                  file: new File([], e.target.value, {
                    type: "application/pdf",
                  }),
                }))
              }
              placeholder="https://example.com/rhp.pdf"
              readOnly={false}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleRHPModalClose}
              disabled={rhpUploading}
              className="h-10 px-6"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleRHPApply}
              disabled={rhpUploading || !rhpUploadData.file}
              className="h-10 gap-2 bg-primary px-6 text-white hover:bg-primary/95"
            >
              {rhpUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                "Apply"
              )}
            </Button>
          </div>
        </div>
      </CommonModal>
    </div>
  );
};

export default AdminIPOForm;
